import { trpc } from './trpc';
import { inferRouterOutputs } from '@trpc/server';
import { AppRouter } from '@tagadapay/trpc-types';
import { useToast } from '@/components/ui/use-toast';

type OutputRouter = inferRouterOutputs<AppRouter>
export type PaymentListOutput = OutputRouter['payments']['list']
export type Payment = PaymentListOutput['payments'][number]
export type PaymentDetails = OutputRouter['payments']['details']
/** Per-payment results of a refund on more than one payment (a single refund answers `null`). */
export type BulkRefundResult = NonNullable<OutputRouter['payments']['refund']>

// Clean payments API using tRPC - types automatically inferred from backend!
export function usePaymentsList(
    params?: Parameters<typeof trpc.payments.list.useQuery>[0]
) {
    return trpc.payments.list.useQuery(params || {
        sortBy: { field: 'createdAt', direction: 'desc' },
        pagination: { page: 1, pageSize: 20 },
        filters: {},
    });

}

export const usePaymentDetails = (id: string) => {
    return trpc.payments.details.useQuery({ id });
}

export const useRefundPayment = () => {
    const utils = trpc.useUtils();
    return trpc.payments.refund.useMutation({
        onSuccess: () => {
            utils.payments.list.invalidate();
            utils.payments.details.invalidate();
            // Note: Toast is handled by the calling component for better UX
        },
        // Note: Error handling is done by the calling component
        
        /**
         * Disable automatic retries to avoid duplicate refund attempts to never silently replay a refund/void
         * 
         * Since disabling retries on the on the general mutation level will affect 300+ mutations, 
         * we are disabling retries only for the refund action to prevent sporadic network errors that will be harder to monitor and debug.
         * 
         * source: https://linear.app/tagadapay/issue/TAG-1732/crm-react-query-retries-refundvoid-mutations-once-on-500-duplicate
         */
        retry: false,  
    });
}

export const useVerifyOnHoldPayment = () => {
    const utils = trpc.useUtils();
    return trpc.payments.verifyOnHoldPayment.useMutation({
        onSuccess: () => {
            utils.payments.list.invalidate();
            utils.payments.details.invalidate();
        },
    });
}

export function useExportPaymentsCsv() {
    const { toast } = useToast();
    return trpc.payments.exportCsv.useMutation({
        onError: (error) => {
            toast({
                title: 'Error',
                description: error.message,
                variant: 'destructive',
            });
        },
    });
}